// M005-F0055 (M005-D005 §8.1, WEB-BKHUB-026) — read the WHOLE calendar
// window, honestly, against Core's pagination contract.
//
// WebUI #101 (Core #162 / !654 follow-up): BookingsCalendar.js used to call
// `GET /api/bookings?org=&start=&end=` with no `limit` and never read
// `meta.total`, so any window holding more than Core's default page of 50
// rows rendered a silent subset — no error, no indicator, rows simply
// absent. Core's window is now overlap-based and series-aware (every
// series active in the week is returned, not just those anchored in it),
// so a busy studio crosses 50 easily.
//
// Core's contract (studioaeye-backend src/routes/booking.routes.ts
// `router.get('/')`, src/services/booking.service.ts `listBookings`):
//   - `limit`  — positive int, default 50, clamped to MAX_PAGINATION_LIMIT
//                = 100 (BUG-010; deliberate, never widened from here);
//   - `offset` — non-negative int, default 0, OFFSET-based (not cursor),
//                bounded by utils/paginationQuery MAX_PAGINATION_OFFSET;
//   - rows are `ORDER BY b.start_time ASC LIMIT ? OFFSET ?`;
//   - the envelope is `{ success, data: [...], meta: { total, limit, offset } }`
//     where `total` is `COUNT(*)` over the SAME where-clause as the page.
//
// This module pages `offset` forward in `limit=100` steps until
// `offset + rows >= meta.total`, and reports whether the window it read is
// COMPLETE. The calendar never renders a window it knows to be incomplete:
// an incomplete read (page cap hit, or the row count still short of the
// count Core reported — e.g. a row inserted before our offset mid-read)
// is surfaced by the component as an explicit state, not a partial grid.
//
// Pure: the page reader is injected so the paging logic is unit-tested
// (and mutation-proven) without a component mount.
import { STUDIO_BOOKINGS_LIMIT, studioBookingsMeta } from './studioBookingsView';

// The same explicit cap the Studio tab asks for — Core's MAX_PAGINATION_LIMIT.
export const CALENDAR_PAGE_LIMIT = STUDIO_BOOKINGS_LIMIT;

// Hard ceiling on pages per window read (50 × 100 = 5,000 rows for a
// single week/month grid — far beyond any real studio). Hitting it is
// reported as `complete: false`, never silently rendered.
export const CALENDAR_MAX_PAGES = 50;

const responseList = (response) => response?.data?.data ?? response?.data ?? [];

/**
 * Read every page of one calendar window.
 *
 * @param {{ org: string|number, start: string, end: string }} window
 * @param {(params: object) => Promise<object>} fetchPage — resolves to an
 *   axios-shaped response for one `GET /api/bookings` page; rejects on a
 *   transport/HTTP failure (the caller decides what a failed read shows).
 * @returns {Promise<{ rows: object[], total: number, complete: boolean }>}
 *   `rows` is every unique row read (keyed by `id`), `total` the count Core
 *   last reported (or the rows read when no meta came back), `complete`
 *   whether `rows` covers that count.
 */
export const readWholeCalendarWindow = async ({ org, start, end }, fetchPage) => {
  const rows = [];
  const seenIds = new Set();
  let offset = 0;
  let total = null;

  for (let page = 0; page < CALENDAR_MAX_PAGES; page += 1) {
    // Pages are sequential by contract: each offset depends on the previous page.
    const response = await fetchPage({ org, start, end, limit: CALENDAR_PAGE_LIMIT, offset });
    const pageRows = responseList(response);
    const meta = studioBookingsMeta(response);
    if (meta) total = meta.total;

    pageRows.forEach((row) => {
      // Offset paging over a live table can hand the same row back twice
      // when a row is inserted ahead of our offset between pages; keep one.
      const id = row?.id;
      if (id != null) {
        if (seenIds.has(id)) return;
        seenIds.add(id);
      }
      rows.push(row);
    });
    offset += pageRows.length;

    // With a reported total, page until the count is covered (an empty
    // page with rows still owed means the count moved under us — stop and
    // report the shortfall rather than spin). Without meta (older envelope,
    // unit fixtures) a short page is the only end-of-data signal there is.
    const done = total === null
      ? pageRows.length < CALENDAR_PAGE_LIMIT
      : (offset >= total || pageRows.length === 0);
    if (done) {
      const known = total === null ? rows.length : total;
      return { rows, total: known, complete: rows.length >= known };
    }
  }

  return { rows, total: total === null ? rows.length : total, complete: false };
};
